'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';

import CommandCenter from './CommandCenter';
import NotificationCenter from './NotificationCenter';

interface AppNavProps {
  variant?: 'landing' | 'app';
  activePath?: string;
}

const navItems = [
  { label: 'Dashboard', href: '/dashboard', icon: '⬡' },
  { label: 'Advisor Portal', href: '/advisor', icon: '⊞' },
  { label: 'Business Passport', href: '/business', icon: '◈' },
  { label: 'Register Company', href: '/register', icon: '🏢' },
  { label: 'Trademark', href: '/trademark', icon: '™' },
  { label: 'TM Watch', href: '/trademark/watch', icon: '👁' },
  { label: 'TM Status', href: '/trademark/status', icon: '⊙' },
  { label: 'G&S Writer', href: '/trademark/writer', icon: '✍️' },
  { label: 'GST', href: '/gst', icon: '₹' },
  { label: 'MSME', href: '/msme', icon: '◫' },
  { label: 'DPIIT', href: '/dpiit', icon: '🇮🇳' },
  { label: 'Examination', href: '/examination', icon: '⊕' },
  { label: 'Pre-Filing Review', href: '/review', icon: '🔍' },
  { label: 'Documents', href: '/documents', icon: '◻' },
  { label: 'Compliance Center', href: '/compliance', icon: '✓' },
  { label: 'Automation', href: '/compliance/automation', icon: '⚡' },
  { label: 'Lexigen AI', href: '/assistant', icon: '◎' },
  { label: 'Checkout', href: '/checkout', icon: '💳' },
];

const UNREAD_COUNT = 4;

// Premium LEXIGEN AI logo mark
function LexigenLogo({ size = 32, showText = true, textSize = '14px' }: { size?: number; showText?: boolean; textSize?: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div
        className="flex items-center justify-center rounded-xl shadow-sm flex-shrink-0"
        style={{
          width: size,
          height: size,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          border: '1px solid rgba(99,102,241,0.3)',
        }}
      >
        <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 18 18" fill="none">
          <path d="M3 3h5v2H5v8h3v2H3V3z" fill="white" fillOpacity="0.9" />
          <path d="M10 3h5v2h-3v3h3v2h-3v3h3v2h-5V3z" fill="#818cf8" />
          <circle cx="14" cy="4" r="1.5" fill="#6366f1" />
        </svg>
      </div>
      {showText && (
        <div className="flex flex-col leading-none">
          <span className="font-bold text-ink tracking-tight" style={{ fontSize: textSize, letterSpacing: '-0.02em' }}>
            LEXIGEN <span className="text-indigo-600">AI</span>
          </span>
        </div>
      )}
    </div>
  );
}

export { LexigenLogo };

export default function AppNav({ variant = 'app', activePath = '' }: AppNavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userName, setUserName] = useState<string>('');

  // Global keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandOpen(prev => !prev);
      }
      // Close mobile menu on Escape
      if (e.key === 'Escape') {
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Load user name for sidebar
  useEffect(() => {
    if (variant !== 'app') return;
    import('@/lib/supabase/client').then(({ supabase }) => {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user?.user_metadata?.full_name) {
          setUserName(user.user_metadata.full_name);
        } else if (user?.email) {
          setUserName(user.email.split('@')[0]);
        }
      });
    });
  }, [variant]);

  if (variant === 'landing') {
    return (
      <>
        <header className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-border">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link href="/" className="flex items-center gap-2.5">
              <LexigenLogo size={32} textSize="15px" />
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              {['Features', 'Pricing', 'About'].map(item => (
                <a key={item} href="#" className="text-[13px] text-ink-muted hover:text-ink transition-colors font-medium">{item}</a>
              ))}
            </nav>
            <div className="flex items-center gap-3">
              <Link href="/login" className="text-[13px] font-medium text-ink-muted hover:text-ink transition-colors">Sign in</Link>
              <Link href="/onboarding" className="text-white text-[13px] font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition-colors shadow-sm" style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
                Get started
              </Link>
            </div>
          </div>
        </header>
        <CommandCenter open={commandOpen} onClose={() => setCommandOpen(false)} />
      </>
    );
  }

  return (
    <>
      {/* Desktop Sidebar — visible at sm (640px) and above */}
      <aside
        className="hidden sm:flex fixed left-0 top-0 h-full w-60 bg-white border-r border-border flex-col z-40 shadow-sm"
        aria-label="Main navigation"
      >
        {/* Logo */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai rounded-lg">
            <LexigenLogo size={32} textSize="14px" />
          </Link>
        </div>

        {/* Search / Command */}
        <div className="px-4 pt-4">
          <button
            onClick={() => setCommandOpen(true)}
            aria-label="Open command search (Ctrl+K)"
            className="w-full flex items-center gap-2.5 px-3 py-2 bg-surface-2 hover:bg-surface-3 border border-border rounded-xl text-[12px] text-ink-muted transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
          >
            <svg className="w-3.5 h-3.5 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="flex-1 text-left">Search…</span>
            <kbd className="text-[9px] font-medium bg-white border border-border px-1 py-0.5 rounded text-ink-subtle group-hover:border-indigo-300 transition-colors" aria-label="Keyboard shortcut Ctrl K">⌘K</kbd>
          </button>
        </div>

        {/* AI Principle Badge */}
        <div className="mx-4 mt-3 px-3 py-2 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(99,102,241,0.05))', border: '1px solid rgba(99,102,241,0.2)' }}>
          <p className="text-[9px] font-bold uppercase tracking-wider text-indigo-700">AI PREPARES · HUMANS APPROVE</p>
          {userName && <p className="text-[11px] text-slate-600 mt-0.5 truncate">{userName}</p>}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto mt-1" aria-label="App navigation">
          {navItems.map(item => (
            <Link
              key={item.href}
              href={item.href}
              aria-current={activePath === item.href ? 'page' : undefined}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai ${
                activePath === item.href
                  ? 'text-white shadow-sm'
                  : 'text-ink-muted hover:text-ink hover:bg-surface-2'
              }`}
              style={activePath === item.href ? { background: 'linear-gradient(135deg, #4f46e5, #6366f1)' } : {}}
            >
              <span className="text-base w-5 text-center" aria-hidden="true">{item.icon}</span>
              <span className="truncate">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="px-3 pb-4 border-t border-border pt-3 space-y-0.5">
          {/* Notifications */}
          <button
            onClick={() => setNotifOpen(true)}
            aria-label="Open notifications"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-ink-muted hover:text-ink hover:bg-surface-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
          >
            <div className="relative w-5 flex items-center justify-center" aria-hidden="true">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {UNREAD_COUNT > 0 && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-danger rounded-full text-[8px] font-bold text-white flex items-center justify-center">
                  {UNREAD_COUNT}
                </span>
              )}
            </div>
            <span>Notifications</span>
            {UNREAD_COUNT > 0 && (
              <span className="ml-auto text-[10px] font-bold bg-danger/10 text-danger px-1.5 py-0.5 rounded-full">
                {UNREAD_COUNT}
              </span>
            )}
          </button>
          <button
            onClick={async () => {
              const { createClient } = await import('@/lib/supabase/client');
              const supabase = createClient();
              await supabase.auth.signOut();
              window.location.href = '/login';
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-ink-muted hover:text-ink hover:bg-surface-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
            aria-label="Sign out"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile Header — visible below sm (640px) */}
      <header className="sm:hidden fixed top-0 left-0 w-full z-50 bg-white border-b border-border h-14 flex items-center justify-between px-4 shadow-sm">
        <Link href="/" className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai rounded-lg">
          <LexigenLogo size={28} textSize="13px" />
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setNotifOpen(true)}
            aria-label="Open notifications"
            className="relative p-2 rounded-xl text-ink-muted hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {UNREAD_COUNT > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-danger rounded-full" />
            )}
          </button>
          <button
            onClick={() => setMobileOpen(prev => !prev)}
            aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            className="p-2 rounded-xl text-ink-muted hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              {mobileOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Nav Drawer */}
      {mobileOpen && (
        <div
          id="mobile-nav"
          className="sm:hidden fixed inset-0 z-40 flex"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <nav className="relative w-72 max-w-[85vw] bg-white h-full flex flex-col shadow-xl overflow-y-auto">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <LexigenLogo size={28} textSize="13px" />
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation menu"
                className="p-1.5 rounded-lg hover:bg-surface-2 text-ink-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 px-3 py-3 space-y-0.5">
              {navItems.map(item => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  aria-current={activePath === item.href ? 'page' : undefined}
                  className={`flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai ${
                    activePath === item.href
                      ? 'text-white shadow-sm'
                      : 'text-ink-muted hover:text-ink hover:bg-surface-2'
                  }`}
                  style={activePath === item.href ? { background: 'linear-gradient(135deg, #4f46e5, #6366f1)' } : {}}
                >
                  <span className="text-lg w-6 text-center" aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
            <div className="px-3 pb-6 border-t border-border pt-3">
              <button
                onClick={async () => {
                  const { createClient } = await import('@/lib/supabase/client');
                  const supabase = createClient();
                  await supabase.auth.signOut();
                  window.location.href = '/login';
                }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[14px] font-medium text-ink-muted hover:text-ink hover:bg-surface-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign out
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Global Overlays */}
      <CommandCenter open={commandOpen} onClose={() => setCommandOpen(false)} />
      <NotificationCenter open={notifOpen} onClose={() => setNotifOpen(false)} />
    </>
  );
}
